import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirestoreOp } from 'src/app/models/firestore-telemetry.model';
import { environment } from 'src/environments/environment';
import { TokenService } from '../auth/token.service';

export const PERMISSION_DENIED_TELEMETRY_PATH = '/api/v1/client-telemetry/permission-denied';

const MAX_REPORTS_PER_SESSION = 20;
const SAFE_PATH = /^[A-Za-z0-9_\-/.]{1,300}$/;

export const isPermissionDenied = (error: any): boolean =>
  error?.code === 'permission-denied' || error?.rejection?.code === 'permission-denied';

const REPORTED = Symbol('permissionDeniedReported');

// True once a denial has been reported, so the global ErrorHandler does not report it a second time.
export const wasReported = (error: any): boolean => !!(error?.[REPORTED] || error?.rejection?.[REPORTED]);

/**
 * Reports client-side Firestore requests refused by the security rules to welby-micro-service, so a
 * legitimate screen broken by the per-clinic rules shows up in Cloud Logging instead of only in one
 * user's console. Sends a path, the operation and the route: never document data. Temporary: remove
 * once the rules rewrite is closed.
 */
@Injectable({ providedIn: 'root' })
export class PermissionDeniedReporter {
  private readonly seen = new Set<string>();

  constructor(private http: HttpClient, private router: Router, private tokenService: TokenService) {}

  report(path: string | undefined, op: FirestoreOp, error: unknown): void {
    if (!isPermissionDenied(error) || wasReported(error) || this.seen.size >= MAX_REPORTS_PER_SESSION) {
      return;
    }
    const target = (error as any)?.rejection ?? error;
    try {
      Object.defineProperty(target, REPORTED, { value: true });
    } catch {
      // A frozen or primitive error just gets no mark; the handler may then report it once more.
    }
    // Without a live session the report would 401; the endpoint is exempt from sign-out, but skip it anyway.
    if (!this.tokenService.getToken() || this.tokenService.tokenIsExpired()) {
      return;
    }
    const safePath = path && SAFE_PATH.test(path) ? path : 'unknown';
    const key = `${op}:${safePath}`;
    if (this.seen.has(key)) {
      return;
    }
    this.seen.add(key);
    // Inside a guard the navigation has not committed yet, so router.url is still the previous page.
    const navigation = this.router.getCurrentNavigation();
    const destination = navigation?.finalUrl ?? navigation?.extractedUrl;
    const url = destination ? this.router.serializeUrl(destination) : this.router.url;
    const rawRoute = (url || '').split('?')[0].split('#')[0];
    const route = SAFE_PATH.test(rawRoute) ? rawRoute : undefined;
    this.http
      .post(`${environment.welbyEndpoint}${PERMISSION_DENIED_TELEMETRY_PATH}`, { path: safePath, op, ...(route && { route }) })
      .subscribe({ error: () => undefined });
  }
}
